import React, { useState } from 'react';
import { Button, Form, FormGroup, Label, Input, FormFeedback, Alert } from 'reactstrap';
import { SingleDatePicker } from 'react-dates';
import axios from 'axios';
import MeterPointSelector from '../MeterPointSelector/MeterPointSelector';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './DataServiceNotificationForm.scss';

const EVENT_CODE_OPTIONS = [
  { value: '', label: 'Select Event Code' },
  { value: 'SN-Vacant', label: 'SN-Vacant' },
  { value: 'SN-NoComms', label: 'SN-NoComms' },
  { value: 'SN-RemoteEnabled', label: 'SN-RemoteEnabled' },
  { value: 'SN-RemoteDisabled', label: 'SN-RemoteDisabled' },
  { value: 'SN-OffPeakDec', label: 'SN-OffPeakDec' },
];

const DataServiceNotificationForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    mpan_core: '',
    meter_id: '',
    event_code: '',
    supplier_notification_effective_from_date: null,
    supplier_notification_expiry_date: null,
    additional_information: '',
  });

  const [offPeakDeclarations, setOffPeakDeclarations] = useState([{ date: null, proportion: '', focusedDate: false }]);

  const [focusedEffectiveDate, setFocusedEffectiveDate] = useState(false);
  const [focusedExpiryDate, setFocusedExpiryDate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ visible: false, message: '', color: 'success' });

  // Handle Meter Point change from MeterPointSelector
  const handleMpanChange = (mpanCore) => {
    setFormData((prev) => ({
      ...prev,
      mpan_core: mpanCore || '',
      meter_id: '', // Reset meter_id when Meter Point changes
    }));
    setErrors((prev) => ({ ...prev, mpan_core: null, meter_id: null }));
  };

  // Handle meter change from MeterPointSelector
  const handleMeterChange = (meter) => {
    setFormData((prev) => ({
      ...prev,
      meter_id: meter ? meter.id : '',
    }));
    setErrors((prev) => ({ ...prev, meter_id: null }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for event_code changes
    if (name === 'event_code') {
      const updates = { [name]: value };

      // Reset meter_id if switching to a code that doesn't require it
      if (value !== 'SN-RemoteEnabled' && value !== 'SN-RemoteDisabled') {
        updates.meter_id = '';
      }

      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));

      // Reset off peak declarations if switching away from SN-OffPeakDec
      if (value !== 'SN-OffPeakDec') {
        setOffPeakDeclarations([{ date: null, proportion: '', focusedDate: false }]);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // Handle off peak declarations
  const handleOffPeakDateChange = (index, date) => {
    const updatedDeclarations = [...offPeakDeclarations];
    updatedDeclarations[index].date = date;
    setOffPeakDeclarations(updatedDeclarations);
    setErrors((prev) => ({ ...prev, [`off_peak_declaration_date_${index}`]: null }));
  };

  const handleOffPeakProportionChange = (index, value) => {
    const updatedDeclarations = [...offPeakDeclarations];
    updatedDeclarations[index].proportion = value;
    setOffPeakDeclarations(updatedDeclarations);
    setErrors((prev) => ({ ...prev, [`off_peak_declaration_proportion_${index}`]: null }));
  };

  const handleOffPeakFocusChange = (index, focused) => {
    const updatedDeclarations = [...offPeakDeclarations];
    updatedDeclarations[index].focusedDate = focused;
    setOffPeakDeclarations(updatedDeclarations);
  };

  const addOffPeakDeclaration = () => {
    setOffPeakDeclarations([...offPeakDeclarations, { date: null, proportion: '', focusedDate: false }]);
  };

  const removeOffPeakDeclaration = (index) => {
    if (offPeakDeclarations.length > 1) {
      const updatedDeclarations = offPeakDeclarations.filter((_, i) => i !== index);
      setOffPeakDeclarations(updatedDeclarations);
      // Clean up errors for this index
      const newErrors = { ...errors };
      delete newErrors[`off_peak_declaration_date_${index}`];
      delete newErrors[`off_peak_declaration_proportion_${index}`];
      setErrors(newErrors);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Meter Point is always required
    if (!formData.mpan_core) {
      newErrors.mpan_core = 'Meter Point is required';
    }

    // Event Code is always required
    if (!formData.event_code) {
      newErrors.event_code = 'Event Code is required';
    }

    // Meter ID is required only for SN-RemoteEnabled and SN-RemoteDisabled
    if (formData.event_code === 'SN-RemoteEnabled' || formData.event_code === 'SN-RemoteDisabled') {
      if (!formData.meter_id) {
        newErrors.meter_id = 'Meter ID is required for Remote Enabled/Disabled event codes';
      }
    }

    // supplier_notification_effective_from_date is required for all event codes EXCEPT SN-OffPeakDec
    if (formData.event_code && formData.event_code !== 'SN-OffPeakDec') {
      if (!formData.supplier_notification_effective_from_date) {
        newErrors.supplier_notification_effective_from_date = 'Effective from date is required';
      }
    }

    // supplier_notification_expiry_date is always optional (no validation needed)

    // off_peak_declaration fields are required only for SN-OffPeakDec event code
    if (formData.event_code === 'SN-OffPeakDec') {
      offPeakDeclarations.forEach((declaration, index) => {
        if (!declaration.date) {
          newErrors[`off_peak_declaration_date_${index}`] = 'Off Peak Declaration Date is required';
        }
        if (!declaration.proportion || declaration.proportion.trim() === '') {
          newErrors[`off_peak_declaration_proportion_${index}`] = 'Off Peak Declaration Proportion is required';
        } else if (!/^\d+$/.test(declaration.proportion)) {
          newErrors[`off_peak_declaration_proportion_${index}`] = 'Must be a valid integer';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.token;

      // Prepare the payload
      const payload = {
        mpan_core: formData.mpan_core,
        meter_id: formData.meter_id,
        event_code: formData.event_code,
        supplier_notification_effective_from_date: formData.supplier_notification_effective_from_date
          ? formData.supplier_notification_effective_from_date.toISOString()
          : null,
        supplier_notification_expiry_date: formData.supplier_notification_expiry_date
          ? formData.supplier_notification_expiry_date.toISOString()
          : null,
        additional_information: formData.additional_information || null,
        off_peak_declarations:
          formData.event_code === 'SN-OffPeakDec'
            ? offPeakDeclarations
                .filter((d) => d.date && d.proportion)
                .map((d) => ({
                  off_peak_declaration_date: d.date.toISOString(),
                  off_peak_declaration_proportion: parseInt(d.proportion, 10),
                }))
            : null,
      };

      await axios.post('/df/dataflows/rest/v1/data-service-notification/submit', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Show success message
      setAlert({
        visible: true,
        message: 'Data Service Notification submitted successfully!',
        color: 'success',
      });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          mpan_core: '',
          meter_id: '',
          event_code: '',
          supplier_notification_effective_from_date: null,
          supplier_notification_expiry_date: null,
          additional_information: '',
        });
        setOffPeakDeclarations([{ date: null, proportion: '', focusedDate: false }]);
        setAlert((prev) => ({ ...prev, visible: false }));
      }, 3000);
    } catch (error) {
      // Error handling
      let errorMessage = 'Failed to submit Data Service Notification. ';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMessage += 'Invalid request data. Please check your inputs.';
        } else if (status === 401) {
          errorMessage += 'Unauthorized. Please log in again.';
        } else if (status === 403) {
          errorMessage += 'You do not have permission to perform this action.';
        } else if (status === 500) {
          errorMessage += 'Server error. Please try again later.';
        } else {
          errorMessage += (data && data.message) || `Error ${status}`;
        }
      } else if (error.request) {
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        errorMessage += error.message;
      }

      setAlert({
        visible: true,
        message: errorMessage,
        color: 'danger',
      });

      // Hide error alert after 8 seconds
      setTimeout(() => {
        setAlert((prev) => ({ ...prev, visible: false }));
      }, 8000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='data-service-notification-form'>
      {alert.visible && (
        <Alert color={alert.color} toggle={() => setAlert((prev) => ({ ...prev, visible: false }))}>
          {alert.message}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        {/* Meter Point and Meter ID using MeterPointSelector */}
        <FormGroup>
          <Label for='mpan_core'>Meter Point *</Label>
          <MeterPointSelector
            value={formData.mpan_core}
            onChange={handleMpanChange}
            placeholder='Search for Meter Point'
            statusFilter='L'
            includeAllStatuses={false}
            includeMeterDropdown={
              formData.event_code === 'SN-RemoteEnabled' || formData.event_code === 'SN-RemoteDisabled'
            }
            onMeterChange={handleMeterChange}
            meterValue={formData.meter_id}
          />
          {errors.mpan_core && (
            <FormFeedback style={{ display: 'block', position: 'static' }}>{errors.mpan_core}</FormFeedback>
          )}
          {errors.meter_id && (
            <FormFeedback style={{ display: 'block', position: 'static' }}>{errors.meter_id}</FormFeedback>
          )}
        </FormGroup>

        {/* Event Code Dropdown */}
        <FormGroup>
          <Label for='event_code'>Event Code *</Label>
          <Input
            type='select'
            name='event_code'
            id='event_code'
            value={formData.event_code}
            onChange={handleInputChange}
            invalid={!!errors.event_code}
          >
            {EVENT_CODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
          {errors.event_code && <FormFeedback style={{ position: 'static' }}>{errors.event_code}</FormFeedback>}
          {formData.event_code === 'SN-RemoteEnabled' || formData.event_code === 'SN-RemoteDisabled' ? (
            <small className='form-text text-muted'>Meter ID is required for this event code</small>
          ) : null}
          {formData.event_code === 'SN-OffPeakDec' && (
            <small className='form-text text-muted'>Off Peak Declarations are required for this event code</small>
          )}
        </FormGroup>

        {/* Effective From Date */}
        <FormGroup>
          <Label>
            Supplier Notification Effective From Date
            {formData.event_code && formData.event_code !== 'SN-OffPeakDec' && ' *'}
          </Label>
          <div>
            <SingleDatePicker
              date={formData.supplier_notification_effective_from_date}
              onDateChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  supplier_notification_effective_from_date: date,
                }));
                setErrors((prev) => ({
                  ...prev,
                  supplier_notification_effective_from_date: null,
                }));
              }}
              focused={focusedEffectiveDate}
              onFocusChange={({ focused }) => setFocusedEffectiveDate(focused)}
              id='effective_from_date'
              placeholder='Effective From Date'
              displayFormat='DD/MM/YYYY'
              isOutsideRange={() => false}
              numberOfMonths={1}
            />
          </div>
          {errors.supplier_notification_effective_from_date && (
            <FormFeedback style={{ display: 'block', position: 'static' }}>
              {errors.supplier_notification_effective_from_date}
            </FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Supplier Notification Expiry Date</Label>
          <div>
            <SingleDatePicker
              date={formData.supplier_notification_expiry_date}
              onDateChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  supplier_notification_expiry_date: date,
                }));
                setErrors((prev) => ({
                  ...prev,
                  supplier_notification_expiry_date: null,
                }));
              }}
              focused={focusedExpiryDate}
              onFocusChange={({ focused }) => setFocusedExpiryDate(focused)}
              id='expiry_date'
              placeholder='Expiry Date'
              displayFormat='DD/MM/YYYY'
              isOutsideRange={() => false}
              numberOfMonths={1}
            />
          </div>
          {errors.supplier_notification_expiry_date && (
            <FormFeedback style={{ display: 'block', position: 'static' }}>
              {errors.supplier_notification_expiry_date}
            </FormFeedback>
          )}
        </FormGroup>

        {/* Additional Information Textarea */}
        <FormGroup>
          <Label for='additional_information'>Additional Information</Label>
          <Input
            type='textarea'
            name='additional_information'
            id='additional_information'
            value={formData.additional_information}
            onChange={handleInputChange}
            rows={4}
            placeholder='Enter any additional information...'
          />
        </FormGroup>

        {/* Off Peak Declarations - Multiple entries */}
        {formData.event_code === 'SN-OffPeakDec' && (
          <div className='off-peak-declarations-section'>
            <Label>
              Off Peak Declarations *
              <Button
                type='button'
                color='link'
                size='sm'
                onClick={addOffPeakDeclaration}
                style={{ marginLeft: '10px', padding: '0 5px' }}
              >
                <i className='icon ion-plus-round'></i> Add Declaration
              </Button>
            </Label>

            {offPeakDeclarations.map((declaration, index) => (
              <div
                key={index}
                className='off-peak-declaration-item'
                style={{
                  marginBottom: '15px',
                  paddingBottom: '15px',
                  borderBottom: index < offPeakDeclarations.length - 1 ? '1px solid #e0e0e0' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FormGroup style={{ flex: 1 }}>
                    <Label for={`off_peak_declaration_date_${index}`}>
                      Date {offPeakDeclarations.length > 1 && `#${index + 1}`}
                    </Label>
                    <div>
                      <SingleDatePicker
                        date={declaration.date}
                        onDateChange={(date) => handleOffPeakDateChange(index, date)}
                        focused={declaration.focusedDate}
                        onFocusChange={({ focused }) => handleOffPeakFocusChange(index, focused)}
                        id={`off_peak_declaration_date_${index}`}
                        placeholder='Select Date'
                        displayFormat='DD/MM/YYYY'
                        isOutsideRange={() => false}
                        numberOfMonths={1}
                        anchorDirection='left'
                        openDirection='up'
                        horizontalMargin={0}
                        withPortal={false}
                        withFullScreenPortal={false}
                      />
                    </div>
                    {errors[`off_peak_declaration_date_${index}`] && (
                      <FormFeedback style={{ display: 'block', position: 'static' }}>
                        {errors[`off_peak_declaration_date_${index}`]}
                      </FormFeedback>
                    )}
                  </FormGroup>

                  <FormGroup style={{ flex: 1 }}>
                    <Label for={`off_peak_declaration_proportion_${index}`}>
                      Proportion {offPeakDeclarations.length > 1 && `#${index + 1}`}
                    </Label>
                    <Input
                      type='number'
                      name={`off_peak_declaration_proportion_${index}`}
                      id={`off_peak_declaration_proportion_${index}`}
                      value={declaration.proportion}
                      onChange={(e) => handleOffPeakProportionChange(index, e.target.value)}
                      placeholder='Enter integer value'
                      invalid={!!errors[`off_peak_declaration_proportion_${index}`]}
                      min='0'
                      step='1'
                    />
                    {errors[`off_peak_declaration_proportion_${index}`] && (
                      <FormFeedback style={{ display: 'block', position: 'static' }}>
                        {errors[`off_peak_declaration_proportion_${index}`]}
                      </FormFeedback>
                    )}
                  </FormGroup>

                  {offPeakDeclarations.length > 1 && (
                    <Button
                      type='button'
                      color='danger'
                      size='sm'
                      onClick={() => removeOffPeakDeclaration(index)}
                      style={{ marginTop: '32px' }}
                      title='Remove this declaration'
                    >
                      <i className='icon ion-trash-a'></i>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form Actions */}
        <FormGroup className='form-actions'>
          <Button type='submit' color='primary' disabled={submitting}>
            {submitting && <i className='fa fa-circle-o-notch fa-spin'></i>} {submitting ? 'Submitting...' : 'Submit'}
          </Button>
          &nbsp;&nbsp;
          {onClose && (
            <Button type='button' color='secondary' onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          )}
        </FormGroup>
      </Form>
    </div>
  );
};

export default DataServiceNotificationForm;
